import { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "../store/auth.store";
import { userPreferencesService } from "../services/workflows.service";
import { queueUserQueueViewSave } from "./usePathologistQueueViewPreference";
import {
  decodePendingWorkView,
  DEFAULT_PENDING_WORK_VIEW,
  PENDING_WORK_DEFAULT_KEY,
  PENDING_WORK_VIEW_KEY,
} from "../pages/workflows/pendingWorkView";
import type { PendingWorkView } from "../types/pendingWork";

export function usePendingWorkView(prefixOptions?: string[]) {
  const userId = useAuthStore((state) => state.profile?.id);
  const client = useQueryClient();
  const [failure, setFailure] = useState<{ userId: string; message: string }>();
  const key = useMemo(
    () => ["pending-work-preference", userId, PENDING_WORK_VIEW_KEY],
    [userId]
  );
  const defaultKey = useMemo(
    () => ["pending-work-preference", userId, PENDING_WORK_DEFAULT_KEY],
    [userId]
  );
  const current = useQuery({
    queryKey: key,
    queryFn: () => userPreferencesService.get(PENDING_WORK_VIEW_KEY),
    enabled: Boolean(userId),
    staleTime: Infinity,
  });
  const defaults = useQuery({
    queryKey: defaultKey,
    queryFn: () => userPreferencesService.get(PENDING_WORK_DEFAULT_KEY),
    enabled: Boolean(userId),
    staleTime: Infinity,
  });
  const view = useMemo(
    () =>
      current.data === null
        ? DEFAULT_PENDING_WORK_VIEW
        : decodePendingWorkView(current.data, prefixOptions),
    [current.data, prefixOptions]
  );
  const savedDefault = useMemo(
    () => decodePendingWorkView(defaults.data, prefixOptions),
    [defaults.data, prefixOptions]
  );
  const save = useCallback(
    async (preferenceKey: string, value: Record<string, unknown>) => {
      if (useAuthStore.getState().profile?.id !== userId)
        throw new Error("Account changed");
      await userPreferencesService.save(preferenceKey, value);
    },
    [userId]
  );
  const update = useCallback(
    (next: PendingWorkView) => {
      if (
        !userId ||
        useAuthStore.getState().profile?.id !== userId ||
        !decodePendingWorkView(next)
      )
        return;
      client.setQueryData(key, next);
      setFailure(undefined);
      void queueUserQueueViewSave(
        userId,
        PENDING_WORK_VIEW_KEY,
        next,
        save
      ).catch(() =>
        setFailure({
          userId,
          message:
            "Your current view could not be saved. Try changing it again.",
        })
      );
    },
    [client, key, save, userId]
  );
  const saveDefault = useCallback(async () => {
    if (!userId || !view) return;
    await queueUserQueueViewSave(userId, PENDING_WORK_DEFAULT_KEY, view, save);
    client.setQueryData(defaultKey, view);
  }, [client, defaultKey, save, userId, view]);
  const applyDefault = useCallback(() => {
    if (savedDefault) update(savedDefault);
  }, [savedDefault, update]);
  const reset = useCallback(() => update(DEFAULT_PENDING_WORK_VIEW), [update]);
  return {
    view: view || DEFAULT_PENDING_WORK_VIEW,
    valid: Boolean(view) && !current.isError,
    hydrated: current.isFetched,
    savedDefault,
    defaultInvalid:
      defaults.isError ||
      (defaults.isFetched && defaults.data !== null && !savedDefault),
    defaultHydrated: defaults.isFetched,
    update,
    saveDefault,
    applyDefault,
    reset,
    error: failure && failure.userId === userId ? failure.message : "",
  };
}
